import * as React from 'react'
import { VariantProps, cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Button as DesigConfigButton } from 'design-configuration/components'

const OUTLINE_BASE =
  'relative transition-all duration-300 bg-surface border border-outline-variant hover:border-outline disabled:opacity-50 text-on-primary-inverted fill-on-primary-inverted text-body'

const buttonVariants = cva(
  'flex items-center justify-center rounded-xs transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-primary disabled:opacity-50 disabled:pointer-events-none ring-offset-background',
  {
    variants: {
      colorTheme: {
        primary: 'text-on-primary bg-primary fill-on-primary hover:bg-primary-80 border border-transparent ',
        secondary: 'text-on-primary-inverted fill-on-primary-inverted',
        'inverted-primary':
          'text-on-primary-inverted bg-primary-inverted fill-on-primary-inverted hover:bg-primary-inverted-20',
        'inverted-secondary': 'text-on-primary-inverted-alpha fill-on-primary-inverted',
        icon: 'text-on-primary bg-primary fill-on-primary hover:bg-primary-80 rounded-full',
        'inverted-icon':
          'text-on-primary-inverted bg-primary-inverted-10 fill-on-primary-inverted hover:bg-primary-inverted-20 rounded-full',
        outline: cn(OUTLINE_BASE, 'px-sm py-xs rounded-xs'),
        'outline-icon': cn(OUTLINE_BASE, 'p-sm rounded-full'),
        'outline-inverted':
          'bg-surface-inverted text-on-surface-inverted border-outline-inverted fill-on-surface-inverted-variant p-xs'
      },
      size: {
        default: '',
        small: ''
      },
      branded: {
        true: 'font-sohne uppercase tracking-[1.68px]',
        false: 'font-founders'
      }
    },
    compoundVariants: [
      {
        colorTheme: ['primary', 'secondary', 'inverted-primary', 'inverted-secondary'],
        size: 'default',
        branded: false,
        className: 'py-xs px-sm gap-xs text-lg'
      },
      {
        colorTheme: ['primary', 'secondary', 'inverted-primary', 'inverted-secondary'],
        size: 'small',
        branded: false,
        className: 'py-2xs px-xs gap-2xs text-xs'
      },
      {
        colorTheme: ['primary', 'secondary', 'inverted-primary', 'inverted-secondary'],
        size: 'default',
        branded: true,
        className: 'py-sm px-md gap-xs text-sm font-medium'
      },
      {
        colorTheme: 'secondary',
        branded: false,
        className: 'bg-primary-8 hover:bg-primary-20 border border-transparent'
      },
      {
        colorTheme: 'secondary',
        branded: true,
        className: 'bg-surface border border-on-surface hover:bg-primary hover:text-on-primary hover:fill-on-primary'
      },
      {
        colorTheme: 'inverted-primary',
        branded: false,
        className:
          'bg-surface text-on-surface fill-on-surface hover:bg-primary hover:text-on-surface-inverted hover:fill-on-surface-inverted'
      },
      {
        colorTheme: 'inverted-primary',
        branded: true,
        className:
          'border border-primary-inverted hover:bg-transparent hover:text-primary-inverted hover:fill-primary-inverted'
      },
      {
        colorTheme: 'inverted-secondary',
        branded: false,
        className: 'bg-primary-inverted-10 hover:bg-primary-inverted-20'
      },
      {
        colorTheme: 'inverted-secondary',
        branded: true,
        className:
          'bg-transparent text-on-surface-inverted border border-on-surface-inverted hover:bg-primary-inverted hover:text-on-primary-inverted'
      },
      {
        colorTheme: ['icon', 'inverted-icon'],
        size: 'default',
        className: 'p-xs'
      },
      {
        colorTheme: ['icon', 'inverted-icon'],
        size: 'small',
        className: 'p-2xs'
      }
    ],
    defaultVariants: {
      size: 'default',
      colorTheme: 'primary',
      branded: false
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  iconLeft?: React.ReactElement
  iconRight?: React.ReactElement
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ colorTheme, className, size, branded, iconLeft, iconRight, ...props }: ButtonProps, ref) => {
    return (
      <DesigConfigButton className={cn(buttonVariants({ colorTheme, size, branded }), className)} ref={ref} {...props}>
        {iconLeft ? <span>{iconLeft}</span> : null}
        <span className="font-[inherit] tracking-[inherit]">{props.children}</span>
        {iconRight ? <span>{iconRight}</span> : null}
      </DesigConfigButton>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
